import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { WorkOrderProposal } from '@syndiqia/agents/contracts';
import type { RequestDetail } from '@syndiqia/comms/contracts';
import { useApi } from '../../api';
import { useI18n } from '../../i18n';
import { useAssets, useMaintenanceMutation, useVendors, useWorkOrders } from '../../maintenance-queries';
import { useSyndicate } from '../../session';
import { WorkOrderForm } from './WorkOrdersPage';

/** On a member request (board view): the work orders it led to, the agent's proposal, and creating one. */
export function RequestWorkOrders({ request }: { request: RequestDetail }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const m = t.maintenance;
  const orders = useWorkOrders('all', request.id);
  const proposal = useProposal(request);
  const [creating, setCreating] = useState<'blank' | 'proposal' | null>(null);
  const decide = useMaintenanceMutation<{ id: string; status: 'used' | 'dismissed' }, void>((api, base, { id, status }) =>
    api(`${base.replace(/\/maintenance$/, '/agents')}/suggestions/${id}`, { method: 'POST', body: { status } }),
  );
  const linked = orders.data ?? [];
  const manages = s.can('maintenance.manage');
  const p = proposal.data;
  if (linked.length === 0 && !manages) return null;
  const showProposal = manages && p?.status === 'proposed' && linked.length === 0;

  return (
    <section className="panel">
      <h2>{m.requestOrders}</h2>
      {linked.length > 0 && (
        <ul className="list">
          {linked.map((w) => (
            <li key={w.id}>
              <Link to={`${s.base}/maintenance/work-orders/${w.id}`}>
                {m.number(w.number)} — {w.title}
              </Link>{' '}
              <span className={`badge badge-wo-${w.status}`}>{m.statuses[w.status]}</span>
              {w.vendorName && <span className="muted small"> · {w.vendorName}</span>}
            </li>
          ))}
        </ul>
      )}
      {p?.status === 'dismissed' && linked.length === 0 && <p className="muted small">✦ {m.proposalDismissed}</p>}

      {showProposal && creating === null && <ProposalCard proposal={p} onUse={() => setCreating('proposal')} onDismiss={() => decide.mutate({ id: p.id, status: 'dismissed' })} />}

      {manages && creating !== null && (
        <WorkOrderForm
          onDone={() => setCreating(null)}
          onCreated={() => creating === 'proposal' && p && decide.mutate({ id: p.id, status: 'used' })}
          initial={
            creating === 'proposal' && p
              ? { title: p.title, description: p.description, priority: p.priority, requestId: request.id, assetId: p.assetId, vendorId: p.vendorId }
              : { title: request.subject, description: request.messages[0]?.body ?? '', priority: request.priority, requestId: request.id }
          }
        />
      )}
      {manages && creating === null && (
        <button type="button" className="button" onClick={() => setCreating('blank')}>
          {m.createFromRequest}
        </button>
      )}
    </section>
  );
}

function ProposalCard({ proposal: p, onUse, onDismiss }: { proposal: WorkOrderProposal; onUse: () => void; onDismiss: () => void }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const m = t.maintenance;
  const assets = useAssets();
  const vendors = useVendors();
  const [copied, setCopied] = useState(false);
  const vendor = vendors.data?.find((v) => v.id === p.vendorId);
  const asset = assets.data?.find((a) => a.id === p.assetId);
  const tradeLabel = (m.trades as Record<string, string>)[p.trade] ?? p.trade;
  const mailto = vendor?.email ? `mailto:${vendor.email}?subject=${encodeURIComponent(`${s.name} — ${p.title}`)}&body=${encodeURIComponent(p.vendorMessage)}` : null;
  const copy = async () => {
    await navigator.clipboard?.writeText(p.vendorMessage);
    setCopied(true);
  };

  return (
    <div className="suggestion-panel proposal">
      <h3>✦ {m.proposalTitle}</h3>
      <p className="muted small">{m.proposalHint}</p>
      <p>
        <strong>{p.title}</strong>
        {p.priority === 'urgent' && <span className="badge badge-urgent">{m.urgent}</span>}
      </p>
      {p.description && <p className="small">{p.description}</p>}
      <dl className="facts">
        <dt>{m.asset}</dt>
        <dd>{asset?.name ?? m.none}</dd>
        <dt>{m.trade}</dt>
        <dd>{tradeLabel}</dd>
        <dt>{m.vendor}</dt>
        <dd>{vendor?.name ?? <span className="muted">{m.noVendorFound}</span>}</dd>
        <dt>{m.why}</dt>
        <dd className="muted">{p.reason}</dd>
      </dl>
      <details>
        <summary>{m.vendorMessage}</summary>
        <p className="message-body small">{p.vendorMessage}</p>
        <div className="form-actions">
          {mailto && (
            <a className="button button-small" href={mailto}>
              ✉ {m.emailVendor}
            </a>
          )}
          <button type="button" className="button button-small" onClick={copy}>
            {copied ? m.copied : m.copy}
          </button>
        </div>
      </details>
      <div className="form-actions">
        <button type="button" className="button button-primary" onClick={onUse}>
          {m.useProposal}
        </button>
        <button type="button" className="button" onClick={onDismiss}>
          {m.dismissProposal}
        </button>
      </div>
    </div>
  );
}

function useProposal(request: RequestDetail) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'maintenance', 'proposal', request.id],
    queryFn: async () => (await api<{ proposal: WorkOrderProposal | null }>(`${s.api}/agents/requests/${request.id}/work-order-proposal`)).proposal,
    enabled: s.can('maintenance.read'),
    // The agent works right after triage: check again for a short while on new maintenance requests.
    refetchInterval: (q) => (q.state.data || request.category !== 'maintenance' || Date.now() - Date.parse(request.createdAt) > 10 * 60_000 ? false : 15_000),
  });
}
